import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  DataTable,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  TextInput,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';

interface AliasTarget {
  id: string;
  providerKey: string;
  accountLabel: string;
  upstreamModel: string;
  priority: number;
  weight: number;
  state: string;
  environment: string;
}

interface Alias {
  id: string;
  alias: string;
  description: string | null;
  isActive: boolean;
  targets: AliasTarget[];
}

export function ModelAliasesPage({ role }: { role: string }) {
  const aliases = useResource<{ items: Alias[] }>('/api/admin/model-aliases');
  const accounts = useResource<{
    items: Array<{
      id: string;
      providerKey: string;
      label: string;
      environment: string;
      providerType: string;
    }>;
  }>('/api/admin/provider-accounts');
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [aliasForm, setAliasForm] = useState({ alias: '', description: '' });
  const [targetFor, setTargetFor] = useState<Alias | null>(null);
  const [targetForm, setTargetForm] = useState({
    providerAccountId: '',
    upstreamModel: '',
    priority: 100,
    environment: 'test',
  });
  const canWrite = role === 'OWNER' || role === 'ADMIN';

  return (
    <>
      <PageHeader
        title="Aliasy modeli"
        description="Klient podaje alias (np. general-fast), nigdy nazwę modelu dostawcy. Kolejność celów wyznacza kolejność failoveru."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              Dodaj alias
            </Button>
          ) : undefined
        }
      />

      <Callout>
        Alias jest kontraktem dla aplikacji: możesz zmienić dostawcę lub model bez żadnej zmiany w
        kodzie klienta.
      </Callout>

      {aliases.loading ? (
        <Skeleton rows={5} />
      ) : (aliases.data?.items ?? []).length === 0 ? (
        <Card>
          <EmptyState
            title="Brak aliasów"
            description="Bez aliasu endpointy zgodne z OpenAI odrzucą żądanie."
          />
        </Card>
      ) : (
        (aliases.data?.items ?? []).map((alias) => (
          <Card
            key={alias.id}
            title={<span className="ah-mono">{alias.alias}</span>}
            description={alias.description ?? undefined}
            actions={
              canWrite ? (
                <Button
                  size="sm"
                  onClick={() => {
                    setTargetFor(alias);
                    setTargetForm({
                      providerAccountId: '',
                      upstreamModel: '',
                      priority: 100,
                      environment: 'test',
                    });
                  }}
                >
                  Dodaj cel
                </Button>
              ) : undefined
            }
            tight
          >
            <DataTable
              rows={alias.targets}
              rowKey={(row) => row.id}
              empty={
                <EmptyState
                  title="Brak celów"
                  description="Alias bez celów zwróci NO_HEALTHY_TARGET."
                />
              }
              columns={[
                {
                  key: 'account',
                  header: 'Konto',
                  render: (row) => (
                    <span className="ah-mono">
                      {row.providerKey}/{row.accountLabel}
                    </span>
                  ),
                },
                {
                  key: 'model',
                  header: 'Model upstream',
                  render: (row) => <span className="ah-mono">{row.upstreamModel}</span>,
                },
                { key: 'env', header: 'Środowisko', render: (row) => row.environment },
                {
                  key: 'priority',
                  header: 'Priorytet',
                  align: 'right',
                  render: (row) => row.priority,
                },
                {
                  key: 'state',
                  header: 'Stan',
                  render: (row) => (
                    <Badge tone={row.state === 'ENABLED' ? 'ok' : 'neutral'}>{row.state}</Badge>
                  ),
                },
                {
                  key: 'actions',
                  header: '',
                  align: 'right',
                  render: (row) =>
                    canWrite ? (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={async () => {
                          try {
                            await api.delete(`/api/admin/model-alias-targets/${row.id}`);
                            aliases.reload();
                          } catch (error) {
                            const described = describeError(error);
                            toast.error(described.title, described.body);
                          }
                        }}
                      >
                        Usuń
                      </Button>
                    ) : null,
                },
              ]}
            />
          </Card>
        ))
      )}

      <Modal
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="Nowy alias modelu"
        footer={
          <>
            <Button onClick={() => setCreateOpen(false)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={!aliasForm.alias}
              onClick={async () => {
                try {
                  await api.post('/api/admin/model-aliases', {
                    alias: aliasForm.alias,
                    ...(aliasForm.description ? { description: aliasForm.description } : {}),
                  });
                  toast.success('Alias utworzony', aliasForm.alias);
                  setCreateOpen(false);
                  setAliasForm({ alias: '', description: '' });
                  aliases.reload();
                } catch (error) {
                  const described = describeError(error);
                  toast.error(described.title, described.body);
                }
              }}
            >
              Utwórz
            </Button>
          </>
        }
      >
        <Field label="Alias" hint="Małe litery i myślniki, np. general-fast.">
          {(id) => (
            <TextInput
              id={id}
              value={aliasForm.alias}
              onChange={(event) => setAliasForm({ ...aliasForm, alias: event.target.value })}
            />
          )}
        </Field>
        <Field label="Opis">
          {(id) => (
            <TextInput
              id={id}
              value={aliasForm.description}
              onChange={(event) => setAliasForm({ ...aliasForm, description: event.target.value })}
            />
          )}
        </Field>
      </Modal>

      <Modal
        open={targetFor !== null}
        onOpenChange={(open) => !open && setTargetFor(null)}
        title="Cel aliasu"
        description={targetFor ? `Alias ${targetFor.alias}` : ''}
        footer={
          <>
            <Button onClick={() => setTargetFor(null)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={!targetForm.providerAccountId || !targetForm.upstreamModel}
              onClick={async () => {
                if (!targetFor) return;
                try {
                  await api.post(`/api/admin/model-aliases/${targetFor.id}/targets`, {
                    providerAccountId: targetForm.providerAccountId,
                    upstreamModel: targetForm.upstreamModel,
                    priority: Number(targetForm.priority),
                    environment: targetForm.environment,
                  });
                  toast.success('Cel aliasu zapisany');
                  setTargetFor(null);
                  aliases.reload();
                } catch (error) {
                  const described = describeError(error);
                  toast.error(described.title, described.body);
                }
              }}
            >
              Zapisz
            </Button>
          </>
        }
      >
        <Field label="Konto dostawcy">
          {(id) => (
            <Select
              id={id}
              value={targetForm.providerAccountId}
              onChange={(event) =>
                setTargetForm({ ...targetForm, providerAccountId: event.target.value })
              }
            >
              <option value="">— wybierz —</option>
              {(accounts.data?.items ?? []).map((account) => (
                <option key={account.id} value={account.id}>
                  {account.providerKey}/{account.label} ({account.environment},{' '}
                  {account.providerType})
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Model upstream">
          {(id) => (
            <TextInput
              id={id}
              value={targetForm.upstreamModel}
              onChange={(event) =>
                setTargetForm({ ...targetForm, upstreamModel: event.target.value })
              }
              placeholder="gpt-5-mini"
            />
          )}
        </Field>
        <Field label="Środowisko">
          {(id) => (
            <Select
              id={id}
              value={targetForm.environment}
              onChange={(event) =>
                setTargetForm({ ...targetForm, environment: event.target.value })
              }
            >
              <option value="test">test</option>
              <option value="staging">staging</option>
              <option value="production">production</option>
            </Select>
          )}
        </Field>
        <Field label="Priorytet">
          {(id) => (
            <TextInput
              id={id}
              type="number"
              value={targetForm.priority}
              onChange={(event) =>
                setTargetForm({ ...targetForm, priority: Number(event.target.value) })
              }
            />
          )}
        </Field>
      </Modal>
    </>
  );
}
